import { runOwsRequestWithMonitor, Feature } from '../../../shared';
import {
    GetFolderRequest,
    getServiceAsync,
    EffectiveRightsType,
    FolderInfoResponseMessage,
} from '../../../service';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import type BaseFolderType from 'owa-service/lib/contract/BaseFolderType';

export type GetFolderData = {
    folderId: string;
    effectiveRights: EffectiveRightsType;
    folderSmtpAddress: string;
};

const HEADER_EXPLICIT_LOGON_USER: string = 'X-OWA-ExplicitLogonUser';
const HEADER_ANCHOR_MAILBOX: string = 'X-AnchorMailbox';

/**
 * Retrieves information about Public Folders
 * @param folderIds Folder ids of the public folders
 * @param additionalLogProperties Additional log properties
 */
export function getOwsFolders(
    folderIds: string[],
    publicFolderMailbox: string,
    additionalLogProperties?: {}
): Promise<GetFolderData[]> {
    return getServiceAsync().then(service => {
        let request: GetFolderRequest = service.Factory.getFolderRequest({
            FolderShape: {
                BaseShape: 'IdOnly',
                AdditionalProperties: [service.Factory.propertyUri({ FieldURI: 'ReplicaList' })],
            },
            FolderIds: folderIds.map(folderId => {
                return service.Factory.folderId({ Id: folderId });
            }),
            ShapeName: 'Folder',
        });

        return runOwsRequestWithMonitor({
            scenarioName: 'GetFolder',
            feature: Feature.ContactManagement,
            operation: () =>
                service.Operation.getFolderOperation(
                    {
                        Header: getJsonRequestHeader(),
                        Body: request,
                    },
                    getExplicitUserHeaders(publicFolderMailbox)
                ),
            qoSConfig: additionalLogProperties && {
                additionalQoSLogProperty: additionalLogProperties,
            },
            transformResponse: response => {
                const folders: GetFolderData[] = [];
                response.Body.ResponseMessages.Items.forEach((item: FolderInfoResponseMessage) => {
                    item.Folders.forEach((folder: BaseFolderType) => {
                        folders.push({
                            folderId: folder.FolderId.Id,
                            effectiveRights: { ...folder.EffectiveRights },
                            folderSmtpAddress: folder.ReplicaList[0],
                        });
                    });
                });

                return folders;
            },
        });
    });
}

function getExplicitUserHeaders(publicFolderMailbox: string) {
    return {
        headers: <any>{
            [HEADER_EXPLICIT_LOGON_USER]: publicFolderMailbox,
            [HEADER_ANCHOR_MAILBOX]: publicFolderMailbox,
            returnFullResponseOnSuccess: true,
        },
    };
}
